import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { throttle, delay } from 'lodash';
import { I18n } from '@fiverr-private/fiverr_context';
import Image from '../../components/Image';
import CloudinaryPdf from '../../components/CloudinaryPdf';
import PinchToZoom from '../../components/PinchToZoom';
import GigReview from '../../GigReview';

class PdfSlide extends PureComponent {

    constructor(props) {
        super(props);

        // Bindings
        this.showReview = this.showReview.bind(this);
        this.hideReview = this.hideReview.bind(this);
        this.flashReview = this.flashReview.bind(this);
        this.toggleReview = this.toggleReview.bind(this);
        this.loadPages = this.loadPages.bind(this);
        this.throttledUpdateScrollStatus = throttle(this.updateScrollStatus.bind(this), 250);
        this.handlePageLoaded = this.handlePageLoaded.bind(this);
        this.renderReview = this.renderReview.bind(this);
        this.renderThumbnail = this.renderThumbnail.bind(this);
        this.renderPages = this.renderPages.bind(this);

        // Refs
        this.wrapper = null;

        // Initial state
        this.state = {
            isShowingReview: false,
            isLoadingMorePages: true,
            isSinglePage: false,
            pageUrls: null,
            isScrollable: false,
            hasBeenScrolled: false
        };
    }

    componentDidMount() {
        const { isActive, expanded, preload } = this.props,
            { pageUrls } = this.state;

        if ((isActive || preload) && expanded && !pageUrls) {
            this.loadPages();
        }

        if (isActive) {
            this.flashReview();
        }
    }

    componentWillReceiveProps(nextProps) {
        const { isActive, expanded } = this.props,
            { pageUrls } = this.state;

        this.updateScrollStatus();

        this.hideReview();
        if (nextProps.isActive && !isActive) {
            this.flashReview();
        }
        if ((nextProps.isActive || nextProps.preload) && expanded && !pageUrls) {
            this.loadPages();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timerId);
    }

    updateScrollStatus() {
        if (!this.wrapper) { return; }
        this.setState((prevState) => {
            if (!prevState.hasBeenScrolled) {
                return {
                    isScrollable: this.wrapper.scrollHeight > this.wrapper.offsetHeight,
                    hasBeenScrolled: this.wrapper.scrollTop > 5
                };
            }
        });
    }

    showReview(autohide) {
        const { slide: { review } } = this.props;
        if (!review) { return; }
        clearTimeout(this.timerId);
        this.setState({ isShowingReview: true });
        if (autohide === true) {
            this.timerId = delay(this.hideReview, 4000);
        }
    }

    hideReview() {
        clearTimeout(this.timerId);
        this.setState({ isShowingReview: false });
    }

    flashReview() {
        this.hideReview();
        this.timerId = delay(this.showReview, 200, true);
    }

    toggleReview() {
        if (this.state.isShowingReview) {
            this.hideReview();
        } else {
            this.showReview();
        }
    }

    loadPages() {
        const { slide: { media } } = this.props;
        const { logger } = this.context;
        const { res, path, assetId } = CloudinaryPdf.getPartsFromUrl(media.pages[0]);
        if (!assetId) {
            // There was an error getting the asset id. Fallback to the default media
            this.setState({
                pageUrls: media.pages || [media.medium],
                isSinglePage: true
            });
            return;
        }
        this.setState({ isLoadingMorePages: true });

        const cloudinary = new CloudinaryPdf(res);
        cloudinary.getPages({
            path,
            assetId,
            width: 1260,
            batchSize: 6,
            onPageLoaded: this.handlePageLoaded
        }).then((pages) => {
            this.setState({
                isLoadingMorePages: false,
                isSinglePage: pages.length === 1
            });
        }).catch(() => {
            logger.error('Failed to load first pdf batch');
        });
    }

    handlePageLoaded(page) {
        this.setState((prevState) => {
            const pageUrls = prevState.pageUrls ? [...prevState.pageUrls] : [];
            pageUrls[page.index] = page.src;
            return {
                pageUrls,
                isLoadingMorePages: true
            };
        });
        this.throttledUpdateScrollStatus();
    }

    renderReview() {
        const { slide: { review }, expanded } = this.props;

        return review ?
            <GigReview {...review} compact={!expanded}/> :
            null;
    }

    renderPages() {
        // Render a list of images that represent the pages in the pdf or fallback to the medium thumbanil.
        const { isActive, onZoomActive, onZoomIdle, preload } = this.props,
            { pageUrls, isLoadingMorePages, isSinglePage } = this.state,
            lazyLoad = !isActive && !preload,
            pages = pageUrls ? pageUrls.map((page, i) => (
                <li key={`${i}--${page}`}>
                    <Image lazyLoad={lazyLoad} isActive={isActive} alt="" src={page} onClick={this.toggleReview}/>
                </li>
            )) : null,
            loadingMore = isLoadingMorePages ? (
                <li key="loading-more" className="loading-more"/>
            ) : null,
            classes = classNames('slide-pages', { 'single-page': isSinglePage, 'loading-more': isLoadingMorePages });

        return (pages || loadingMore) ? (
            <div className={classes}>
                <PinchToZoom rotate={false} minScale={1.5} onActive={onZoomActive}
                    onIdle={onZoomIdle} preventSingleTouchEvents={false}>
                    <ul ref={(node) => this.wrapper = node}
                        onScroll={this.throttledUpdateScrollStatus}>
                        { pages }{ loadingMore }
                    </ul>
                </PinchToZoom>
                { this.renderReview() }
            </div>
        ) : null;
    }

    renderThumbnail() {
        const { slide: { name, media }, isActive, preload } = this.props,
            lazyLoad = !isActive && !preload;
        return (
            <figure className="thumbnail" tabIndex="0">
                <Image lazyLoad={lazyLoad} isActive={isActive} alt={name} src={media.medium}/>
            </figure>
        );
    }

    render() {
        const { onClick, expanded, isActive } = this.props,
            { isShowingReview, isScrollable, hasBeenScrolled } = this.state,
            slideClasses = classNames(
                'slide',
                'slide-pdf',
                {
                    'active-review': isShowingReview && isActive,
                    'scroll-hint': isScrollable && !hasBeenScrolled && !isShowingReview
                }
            );

        return (
            <div className={slideClasses} onClick={onClick}>
                { expanded ? this.renderPages() : this.renderThumbnail() }
                { this.renderReview() }
                { !expanded && <div className="slide-fullscreen">
                    <I18n k={'gig_page_perseus.gallery.view_presentation'}/>
                </div> }
            </div>
        );
    }
}

PdfSlide.propTypes = {
    isActive: PropTypes.bool,
    expanded: PropTypes.bool,
    preload: PropTypes.bool,
    slide: PropTypes.object,
    onZoomActive: PropTypes.func,
    onZoomIdle: PropTypes.func,
    onClick: PropTypes.func
};

PdfSlide.contextTypes = {
    logger: PropTypes.object
};

export default PdfSlide;
